import moment from "moment";
import { globalClasses } from "helper/globalClasses";
import { globalVariables } from "helper/globalVariables";
import { useMediaQuery } from "react-responsive";
import { client } from "helper/apolloConfig";
import { useLazyQuery } from "@apollo/client";
import { useEffect, useState, useRef } from "react";
import { updateDogTrialDetail, getTrialCalendarEntriesByAuthId, deleteDogTrialDetail } from "redux/features/memberSlice/memberGql";
import { getTrialCalendarByIdQuery } from "redux/features/trailResultSlice/trailResultGql";
import { DEFAULT_DATE_FORMAT } from "helper/utility";
import { pencilAlt } from "helper/icons";
import { cross } from "helper/images";
import { TableWithPagination } from "shared/Components/tableWithPagination/index.js";
import { useSelector } from "react-redux";
import Dropdown from "shared/Components/dropdown";
import PageInfo from "shared/Components/pageInfo";
import { orderSearchesElements } from "helper/orderSearchElements";
import { Button } from "shared/Components/button";
import { EditEntriesModal } from "./EditEntriesModal";
import { PopUpMessages } from "shared/Components/popUpMessages";

const { paginatedDataCls } = globalClasses;

const headData = [
  {
    label: globalVariables.trialDate,
    style: "",
  },
  {
    label: globalVariables.trialName,
    style: "",
  },
  {
    label: globalVariables.dog,
    style: "",
  },
  {
    label: globalVariables.enteredIn,
    style: "",
  },
];

export function ViewEntries() {
  const isTabletMobile = useMediaQuery({ query: "(max-width: 1024px)" });
  const [pageNo, setPageNo] = useState(1);
  const { auth } = useSelector((state) => state);
  const editModalRef = useRef();
  const modalRef = useRef();
  const authId = auth?.memberReducer?.memberDetails.authId;  
  const [getTrialEntries, { data, loading, error, refetch }] =
  useLazyQuery(getTrialCalendarEntriesByAuthId, {
    fetchPolicy: "network-only",
  });

  const [getTrialCalendarById, { data: trialCalendarData}] =
  useLazyQuery(getTrialCalendarByIdQuery, {
    fetchPolicy: "network-only",
  });

  const trials = data?.getTrialCalendarEntriesByAuthId?.data;

  const { searches } = trialCalendarData?.getTrialCalendarById ?? [];

  const [apiData, setApiData] = useState({
    title: "",
    desc: "",
    type: "pending",
  });
  
  const [dogSearch, setDogSearch] = useState({
    id: "",
    division: "",
    elements: "",
  });

  const [deletingEntryId, setDeletingEntryId] = useState(null);

  const divisionOptions = ["Standard", "Pro", "FEO"];

  useEffect(() => {
  if (authId) {
    getTrialEntries({
      variables: {
          authId,
        pageNumber: 1,
        pageSize: 10,
      },
    });
  }
  }, [authId]);
  
  const getTrialSearches = (id) => {
    getTrialCalendarById({
      variables: {
        trialCalendarId: +id,
      },
    });
    editModalRef.current.openModal();
  }

  const handlePaginationData = (page = 1) => {
    setPageNo(page);
  };

  const handleApply = async () => {
    try {
      const result = await client.mutate({
        mutation: updateDogTrialDetail,
        variables: {
          dogTrialId: dogSearch.id,
          division: dogSearch.division,
          elements: dogSearch.elements,
        }
      });
  
      if (result?.data?.updateDogTrialDetail) {
        modalRef.current.openModal();
        setApiData({
          ...apiData,
          title: globalVariables.success,
          desc: result.data.updateDogTrialDetail.message,
          type: "success",
        });
        refetch();
      }
    } catch (error) {
      modalRef.current.openModal();
      setApiData({
        ...apiData,
        title: "Ooops!",
        desc: error?.message,
        type: "pending",
      });
    } finally {
      editModalRef.current.closeModal();
    }
  };

  const onRemoveSearch = async (id) => {
    // Prevent multiple clicks - if already deleting this entry, return early
    if (deletingEntryId === id) {
      return;
    }

    // Set the deleting state to disable the button
    setDeletingEntryId(id);

    try {
      const result = await client.mutate({
        mutation: deleteDogTrialDetail,
        variables: {
          dogTrialId: +id,
        }
      });

      if (result?.data?.deleteDogTrialDetail) {
        modalRef.current.openModal();
        setApiData({
          ...apiData,
          title: globalVariables.success,
          desc: result.data.deleteDogTrialDetail.message,
          type: "success",
        });
        refetch();
      }
    } catch (error) {
      modalRef.current.openModal();
      setApiData({
        ...apiData,
        title: "Ooops!",
        desc: error?.message,
        type: "pending",
      });
    } finally {
      // Clear the deleting state to re-enable the button
      setDeletingEntryId(null);
      editModalRef.current.closeModal();
    }
  };

  const isPastCutoff = (cutoffDate) => {
    if (!cutoffDate) return false;
    return new Date() > new Date(cutoffDate);
  };

  const renderTableBody = () =>
    trials?.map((trial, index) => {
      const cutoffPassed = isPastCutoff(trial?.cutoffDate);
      return (
        <tr>
          <td className={paginatedDataCls(index, "min-w-[150px]")}>
            {moment.utc(trial?.trialDate).format(DEFAULT_DATE_FORMAT)}
          </td>

          <td className={paginatedDataCls(index, "min-w-[150px]")}>
            {trial?.trialName || "-"}
          </td>
          <td className={paginatedDataCls(index, "min-w-[150px]")}>
            {trial?.dog}
          </td>
          <td className={`${paginatedDataCls(index, "min-w-[150px]")} flex justify-center`}>
            <div className="flex flex-col gap-3">
              {trial?.entries.map((e, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2"
                >
                  <span className="font-semibold">{e.elements}</span> |
                  <span className="font-semibold">{e.division}</span>
                  {cutoffPassed ? (
                    <span
                      className="text-xs text-red-500 font-medium ml-1"
                      title={`Entry changes closed. Contact the Trial Administrator to make changes.`}
                    >
                      🔒 Closed
                    </span>
                  ) : (
                    <>
                      <img
                        onClick={() => {
                          getTrialSearches(trial.trialCalendarId);
                          setDogSearch(e);
                        }}
                        className="cursor-pointer" src={pencilAlt} alt="Edit entry"
                      />
                      <img
                        onClick={() => {
                          onRemoveSearch(e.id)
                        }}
                        className={deletingEntryId === e.id ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                        style={{ pointerEvents: deletingEntryId === e.id ? "none" : "auto" }}
                        alt="Cancel entry"
                        src={cross}
                        height={32}
                        width={32}
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          </td>
        </tr>
      );
    });

  return (
    <div
      className="App rounded-md  min-h-screen lg:ml-[17rem] sm:pt-8 bg-white lg:p-10"
    >
      <PageInfo 
        title={globalVariables.viewEntries}
      />
      <TableWithPagination
        headData={headData}
        headDateWithStyle={true}
        tableHeight={`${isTabletMobile ? "h-[85vh]" : "h-[600px]"}`}
        tableBody={renderTableBody()}
        itemsPerPage={10}
        totalPage={trials?.totalPages}
        handlePageChange={(page) => handlePaginationData(page)}
        loading={loading}
        current={pageNo}
        error={error}
        persistPagination={true}
      />
      <PopUpMessages
        title={apiData.title}
        desc={apiData.desc}
        type={apiData.type}
        ref={modalRef}
      />
      <EditEntriesModal modalRef={editModalRef}>
        <div className="flex flex-col gap-4">
        <p className="text-md text-900 font-semibold text-center">Are you sure you want to edit the search for {dogSearch.dog}?</p>
          <Dropdown
            width="w-[100%]"
            dropdownOptions={orderSearchesElements(searches, "searchName").map((e) => e?.searchName) ?? []}
            placeholder={globalVariables.selectSearch}
            onChange={(value) =>
              setDogSearch((prev) => ({
                ...prev,
                elements: value
              }))
            }
            defaultOption={dogSearch.elements}
          />
          <Dropdown
            width="w-[100%]"
            dropdownOptions={divisionOptions}
            placeholder={globalVariables.selectSearch}
            onChange={(value) => 
              setDogSearch((prev) => ({
                ...prev,
                division: value
              }))
            }
            defaultOption={dogSearch.division}
          />
          <div className="flex gap-2 justify-center mt-4"> 
            <Button
              onClick={handleApply}
              label="Yes"
              width="w-[120px]"
            />
            <button 
              className="w-[120px] border border-gray-300 rounded-md hover:bg-gray-300"
              onClick={() => editModalRef.current.closeModal()}
              >
              No
            </button>
          </div>
        </div>
      </EditEntriesModal>
    </div>
  );
};
